import {
  Box,
  Collapse,
  FilledInput,
  FormControl,
  FormHelperText,
  Grow,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import GoogleAddressField from '../../../Shared/Form/GoogleAddressField';

// Define FormField interface locally to avoid circular imports
interface FormField {
  Label: string;
  Description: string;
  DataType: string;
  AllowedValues: string[];
  Required: boolean;
  FieldId: string;
  Validation: string;
  Children?: ChildGroup[];
}

interface ChildGroup {
  show_on_value: string | string[];
  fields: FormField[];
}

interface Props {
  form_field: FormField;
  validateField: (
    fieldId: string,
    fieldName: string,
    isValid: boolean,
    error: string,
    fieldValue: any
  ) => void;
  isChild: boolean;
  addressFields?: FormField[]; // Optional array of address fields for mapping
  [dataAttr: string]: any; // for custom data attributes
}

const isEmpty = (v: any) =>
  v === null ||
  v === undefined ||
  (typeof v === 'string' && v.trim() === '') ||
  (Array.isArray(v) && v.length === 0);

const hasAllowedValues = (field: FormField) =>
  (field.AllowedValues?.length ?? 0) > 0;

function DynamicFormField({ form_field, validateField, isChild, addressFields, ...rest }: Props) {
  const [value, setValue] = useState<any>('');
  const [error, setError] = useState('');
  const [isValid, setIsValid] = useState(!form_field.Required);
  const [touched, setTouched] = useState(false);
  const inputRef = useRef<HTMLInputElement | HTMLSelectElement>(null);
  const optionalLabel = !form_field.Required && (
    <Box component="span" sx={{ ml: 0.5, fontSize: '0.8em' }}>
      (Optional)
    </Box>
  );

  // Create a mapping of Google Maps components to our form fields
  const createAddressMapping = (googleAddressObj: Record<string, string | number>) => {
    const fieldMapping: Record<string, any> = {};
    
    if (!addressFields) return fieldMapping;

    // Map Google Maps data to our specific address fields using exact field names
    addressFields.forEach((field: FormField) => {
      const fieldId = field.FieldId;
      const label = field.Label;
      
      // Exact mapping based on known field names
      switch (label) {
        case 'Address':
          fieldMapping[fieldId] = googleAddressObj['Address:Street'] || '';
          break;
        case 'Address 2':
          fieldMapping[fieldId] = googleAddressObj['Address:Line2'] || '';
          break;
        case 'City':
          fieldMapping[fieldId] = googleAddressObj['Address:City'] || '';
          break;
        case 'State':
          fieldMapping[fieldId] = googleAddressObj['Address:State'] || '';
          break;
        case 'Country':
          fieldMapping[fieldId] = googleAddressObj['Address:Country'] || '';
          break;
        case 'Postal':
        case 'Zip':
        case 'Postal Code':
        case 'Zip Code':
          fieldMapping[fieldId] = googleAddressObj['Address:PostalCode'] || '';
          break;
        default:
          // Fallback for any other address field - use full address
          fieldMapping[fieldId] = googleAddressObj['Address:Full'] || '';
          break;
      }
    });

    return fieldMapping;
  };

  // Initialize address fields in validation system when this is an AddressAutocomplete field
  useEffect(() => {
    if (form_field.DataType === 'AddressAutocomplete' && addressFields) {
      addressFields.forEach(field => {
        validateField(field.FieldId, field.Label, !field.Required, '', '');
      });
    }
  }, [form_field.DataType, addressFields, validateField]);

  /* ---------- Validation ---------- */
  const runValidation = useCallback(
    (nextVal: any) => {
      let ok = true;
      let err = '';

      if (form_field.Required && isEmpty(nextVal)) {
        ok = false;
        // (We avoid forcing an error message "This field is required" if you prefer.)
      }

      if (ok && hasAllowedValues(form_field)) {
        if (!form_field.AllowedValues.includes(nextVal)) {
          ok = false;
          if (touched) err = 'Value not allowed.';
        }
      }

      // If there's a custom validation pattern
      if (ok && form_field.Validation) {
        const regex = new RegExp(form_field.Validation);
        if (!regex.test(String(nextVal))) {
          ok = false;
          if (touched) err = 'Does not match required format.';
        }
      }

      setIsValid(ok);
      setError(err);
      validateField(form_field.FieldId, form_field.Label, ok, err, nextVal);
    },
    [form_field, touched, validateField]
  );

  useEffect(() => {
    runValidation(value);
  }, [value, runValidation]);

  // initial registration (once)
  useEffect(() => {
    validateField(
      form_field.FieldId,
      form_field.Label,
      !form_field.Required,
      '',
      ''
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ---------- Handlers ---------- */
  const handleInput = (e: any) => {
    if (!touched) setTouched(true);
    setValue(e.target.value);
  };

  const handleBlur = () => {
    if (!touched) setTouched(true);
    runValidation(value);
  };

  /* ---------- Rendering ---------- */
  const fieldId = `field-${form_field.FieldId}`;
  const helperId = `${fieldId}-hint`;
  const errorId = `${fieldId}-err`;
  const showError = !isValid && touched;

  // Smart Address Autocomplete Field
  if (form_field.DataType === 'AddressAutocomplete') {
    return (
      <Box sx={{ pl: isChild ? 2 : 0 }}>
        <Grow in>
          <FormControl variant="filled" fullWidth error={showError} {...rest}>
            <InputLabel
              htmlFor={fieldId}
              required={form_field.Required}
              id={`${fieldId}-label`}
              shrink
              sx={{ position: 'static', transform: 'none', fontSize: '0.75rem', marginBottom: '4px' }}
            >
              {form_field.Label}
              {optionalLabel}
            </InputLabel>
            <GoogleAddressField
              value={value}
              onChange={(googleAddressObj: Record<string, string | number>) => {
                setValue(googleAddressObj['Address:Full'] || '');
                
                // Map Google address data to our specific address fields
                const fieldMapping = createAddressMapping(googleAddressObj);
                
                Object.entries(fieldMapping).forEach(([fieldId, fieldValue]) => {
                  // Find the field to get its label
                  const field = addressFields?.find(f => f.FieldId === fieldId);
                  const fieldLabel = field?.Label || fieldId;
                  const isRequired = field?.Required || false;
                  const isValid: boolean = !isRequired || (fieldValue != null && String(fieldValue).trim() !== '');
                  
                  validateField(fieldId, fieldLabel, isValid, '', fieldValue);
                });
              }}
            />
            <FormHelperText id={helperId}>{form_field.Description}</FormHelperText>
            {showError && error && <FormHelperText id={errorId}>{error}</FormHelperText>}
          </FormControl>
        </Grow>
      </Box>
    );
  }

  // Legacy Address Field (for backward compatibility)
  if (form_field.DataType === 'Address') {
    return (
      <Box sx={{ pl: isChild ? 2 : 0 }}>
        <Grow in>
          <FormControl variant="filled" fullWidth error={showError} {...rest}>
            <InputLabel
              htmlFor={fieldId}
              required={form_field.Required}
              id={`${fieldId}-label`}
            >
              {form_field.Label}
              {optionalLabel}
            </InputLabel>
            <GoogleAddressField
              value={value}
              onChange={(addressObj: Record<string, string | number>) => {
                setValue(addressObj['Address:Full'] || '');
                
                // Reuse mapping helper
                const fieldMapping = createAddressMapping(addressObj);
                
                // If helper returned empty, fall back to legacy behavior
                const mappingEntries = Object.entries(fieldMapping);
                if (mappingEntries.length > 0) {
                  mappingEntries.forEach(([fieldId, fieldValue]) => {
                    const field = addressFields?.find(f => f.FieldId === fieldId);
                    const fieldLabel = field?.Label || fieldId;
                    const isRequired = field?.Required || false;
                    const isValid = !isRequired || (fieldValue && String(fieldValue).trim() !== '');
                    validateField(fieldId, fieldLabel, isValid, '', fieldValue);
                  });
                } else {
                  // Legacy: map all address subfields directly
                  Object.entries(addressObj).forEach(([subFieldId, val]) => {
                    validateField(subFieldId, subFieldId, !!val, '', val);
                  });
                }
              }}
            />
            <FormHelperText id={helperId}>{form_field.Description}</FormHelperText>
            {showError && error && <FormHelperText id={errorId}>{error}</FormHelperText>}
          </FormControl>
        </Grow>
      </Box>
    );
  }

  return (
    <Box sx={{ pl: isChild ? 2 : 0 }}>
      <Grow in>
        <FormControl variant="filled" fullWidth error={showError} {...rest}>
          <InputLabel
            htmlFor={fieldId}
            required={form_field.Required}
            id={`${fieldId}-label`}
            shrink={form_field.DataType === 'Date' ? true : undefined}
          >
            {form_field.Label}
            {optionalLabel}
          </InputLabel>

          {!hasAllowedValues(form_field) ? (
            <FilledInput
              id={fieldId}
              inputRef={inputRef}
              aria-describedby={`${helperId}${showError ? ` ${errorId}` : ''}`}
              aria-invalid={showError || undefined}
              aria-required={form_field.Required || undefined}
              type={form_field.DataType}
              disableUnderline
              value={value}
              onChange={handleInput}
              onBlur={handleBlur}
            />
          ) : (
            <Select
              labelId={`${fieldId}-label`}
              id={fieldId}
              inputRef={inputRef}
              aria-describedby={`${helperId}${showError ? ` ${errorId}` : ''}`}
              aria-invalid={showError || undefined}
              aria-required={form_field.Required || undefined}
              value={value}
              onChange={handleInput}
              onBlur={handleBlur}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {form_field.AllowedValues.map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </Select>
          )}

          {/* Description */}
          {form_field.Description && (
            <FormHelperText id={helperId}>{form_field.Description}</FormHelperText>
          )}
          {/* Error text if any */}
          {showError && error && <FormHelperText id={errorId}>{error}</FormHelperText>}
        </FormControl>
      </Grow>

      {/* 
        Render each child group in a separate Collapse if the parent's 
        current `value` matches that group's `show_on_value`.
      */}
      {form_field.Children?.map((group: ChildGroup, groupIdx: number) => {
        // If group.show_on_value is an array, check with includes(); 
        // if it's a string, check equality.
        const shouldShow =
          Array.isArray(group.show_on_value)
            ? group.show_on_value.includes(value)
            : group.show_on_value === value;

        return (
          <Collapse in={shouldShow} timeout="auto" unmountOnExit key={groupIdx}>
            <Box
              role="group"
              aria-labelledby={`${fieldId}-label`}
              sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}
            >
              {group.fields.map((childField, idx) => (
                <DynamicFormField
                  key={childField.FieldId}
                  form_field={childField}
                  validateField={validateField}
                  isChild
                  data-first-child-of={idx === 0 ? form_field.FieldId : undefined}
                />
              ))}
            </Box>
          </Collapse>
        );
      })}
    </Box>
  );
}

export default DynamicFormField;
